import { useState } from 'react';
import axios from 'axios';
import { Button } from '../ui/button';
import { Input } from '../ui/input';
import { Textarea } from '../ui/textarea';
import { Label } from '../ui/label';
import { toast } from 'sonner';
import { Loader2, CheckCircle } from 'lucide-react';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export const LeadForm = ({ 
    sourcePage = 'unknown', 
    showServiceSelect = false,
    compact = false,
    className = '' 
}) => {
    const [loading, setLoading] = useState(false);
    const [submitted, setSubmitted] = useState(false);
    const [formData, setFormData] = useState({
        name: '',
        email: '',
        phone: '',
        company: '',
        website: '',
        service_interest: '',
        message: '',
    });

    const handleChange = (e) => {
        setFormData(prev => ({
            ...prev,
            [e.target.name]: e.target.value
        }));
    };

    const handleSubmit = async (e) => {
        e.preventDefault();
        setLoading(true);

        try {
            await axios.post(`${API}/leads`, {
                ...formData,
                source_page: sourcePage
            });
            setSubmitted(true);
            toast.success('Thank you! We\'ll be in touch soon.');
        } catch (error) {
            console.error('Error submitting lead:', error);
            toast.error('Something went wrong. Please try again.');
        } finally {
            setLoading(false);
        }
    };

    if (submitted) {
        return (
            <div className={`text-center py-12 ${className}`} data-testid="lead-form-success">
                <div className="inline-flex items-center justify-center w-16 h-16 rounded-full bg-green-100 dark:bg-green-900/30 mb-6">
                    <CheckCircle className="w-8 h-8 text-green-600 dark:text-green-400" />
                </div>
                <h3 className="text-2xl font-semibold mb-2">Thank You!</h3>
                <p className="text-muted-foreground">
                    We've received your inquiry and will get back to you within 24 hours.
                </p>
            </div>
        );
    }

    return (
        <form onSubmit={handleSubmit} className={`space-y-5 ${className}`} data-testid="lead-form">
            <div className={compact ? 'grid grid-cols-1 md:grid-cols-2 gap-4' : 'space-y-5'}>
                <div className="space-y-2">
                    <Label htmlFor="name">Name *</Label>
                    <Input
                        id="name"
                        name="name"
                        value={formData.name}
                        onChange={handleChange}
                        placeholder="John Doe"
                        required
                        data-testid="lead-form-name"
                    />
                </div>

                <div className="space-y-2">
                    <Label htmlFor="email">Email *</Label>
                    <Input
                        id="email"
                        name="email"
                        type="email"
                        value={formData.email}
                        onChange={handleChange}
                        placeholder="john@company.com"
                        required
                        data-testid="lead-form-email"
                    />
                </div>

                <div className="space-y-2">
                    <Label htmlFor="phone">Phone *</Label>
                    <Input
                        id="phone"
                        name="phone"
                        value={formData.phone}
                        onChange={handleChange}
                        placeholder="+91 98765 43210"
                        required
                        data-testid="lead-form-phone"
                    />
                </div>

                <div className="space-y-2">
                    <Label htmlFor="company">Company</Label>
                    <Input
                        id="company"
                        name="company"
                        value={formData.company}
                        onChange={handleChange}
                        placeholder="Your Company"
                        data-testid="lead-form-company"
                    />
                </div>

                {!compact && (
                    <div className="space-y-2">
                        <Label htmlFor="website">Website</Label>
                        <Input
                            id="website"
                            name="website"
                            value={formData.website}
                            onChange={handleChange}
                            placeholder="https://yourwebsite.com"
                            data-testid="lead-form-website"
                        />
                    </div>
                )}

                {showServiceSelect && (
                    <div className="space-y-2">
                        <Label htmlFor="service_interest">Service Interest</Label>
                        <select
                            id="service_interest"
                            name="service_interest"
                            value={formData.service_interest}
                            onChange={handleChange}
                            className="flex h-12 w-full rounded-lg border border-input bg-transparent px-3 py-1 text-sm shadow-sm transition-colors focus-visible:outline-none focus-visible:ring-1 focus-visible:ring-ring"
                            data-testid="lead-form-service"
                        >
                            <option value="">Select a service</option>
                            <option value="gmb">GMB Optimization</option>
                            <option value="geo-seo">GEO SEO</option>
                            <option value="ai-search">AI Search Optimization</option>
                            <option value="seo">High-Intent SEO</option>
                            <option value="programmatic">Programmatic SEO</option>
                        </select>
                    </div>
                )}
            </div>

            {!compact && (
                <div className="space-y-2">
                    <Label htmlFor="message">Message</Label>
                    <Textarea
                        id="message"
                        name="message"
                        value={formData.message}
                        onChange={handleChange}
                        placeholder="Tell us about your goals..."
                        rows={4}
                        data-testid="lead-form-message"
                    />
                </div>
            )}

            <Button 
                type="submit" 
                className="w-full btn-glow rounded-full h-12 text-base"
                disabled={loading}
                data-testid="lead-form-submit"
            >
                {loading ? (
                    <>
                        <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                        Submitting...
                    </>
                ) : (
                    'Get Free Strategy Session'
                )}
            </Button>
        </form>
    );
};
